/* ──────────────────────────────────────────────────────────────────────────
 * Kumbh geospatial layer — Nashik & Trimbakeshwar.
 *
 * ⚠ READ THIS BEFORE USING ANY FIGURE HERE OPERATIONALLY.
 *
 * Two different kinds of data live in this file, and they are NOT equally
 * trustworthy:
 *
 *   1. PLACE LOCATIONS (Ramkund, Trimbakeshwar temple, Nashik Road station,
 *      Kalaram temple, Gangapur dam…) are real, public, well-known
 *      coordinates, accurate to roughly street level. Marked UNVERIFIED
 *      because nobody on this project has surveyed them — they are good
 *      enough to navigate to, not good enough to deploy against.
 *
 *   2. EVERYTHING ELSE — sector boundaries, gate positions, route geometry,
 *      facility placements, capacities, crowd figures — is INVENTED for the
 *      demo. It is plausible-looking and geographically sane, but it is not
 *      the Simhastha plan. Real sector boundaries come from NTKMA / Nashik
 *      district administration as GeoJSON and must replace this wholesale.
 *
 * Every record carries provenance saying which it is. Nothing here should
 * ever reach an operator without its SIMULATED badge attached (§14).
 * ────────────────────────────────────────────────────────────────────────── */

import {
  withProvenance, TRUST, SOURCE_KIND, CLASSIFICATION,
} from "./provenance";

/* Map home view.
 *
 * Opens over Nashik city at working zoom, NOT at theatre zoom. An earlier
 * default framed both Nashik and Trimbakeshwar — about 35 km across — at
 * which an operational sector is a handful of pixels, no street detail is
 * legible, and the green/amber/red fills read as nothing at all. A control
 * map has to open somewhere an operator can actually work; the wide view is
 * one click away on the Theatre button. */
export const MAP_HOME = { center: [73.7898, 19.9975], zoom: 12.8 };

/* The full operational theatre, both Kumbh centres in frame. */
export const THEATRE_VIEW = { center: [73.700, 19.975], zoom: 10.6 };
export const NASHIK = { center: [73.7898, 19.9975], zoom: 13.2 };
export const TRIMBAK = { center: [73.5310, 19.9403], zoom: 14 };

/* Bounds keep the operator from panning off to another continent. They are
 * deliberately GENEROUS. An earlier, tighter box (0.55° x 0.25°) was narrower
 * than the map viewport at the home zoom, so MapLibre had to fight the
 * constraint on every render — it force-zoomed to fit and the view never
 * settled where intended. Bounds must always be larger than the widest view
 * you expect to show, or they stop being a guard rail and become a bug. */
export const THEATRE_BOUNDS = [[72.95, 19.45], [74.45, 20.55]];

const realPlace = (note) => ({
  source: SOURCE_KIND.OSM,
  trust: TRUST.UNVERIFIED,
  confidence: 0.8,
  owner: "OpenStreetMap contributors",
  classification: CLASSIFICATION.PUBLIC,
  note: note || "Public coordinate, approximate. Not survey-grade.",
});

const demo = (note, classification = CLASSIFICATION.PUBLIC) => ({
  source: SOURCE_KIND.SIMULATION,
  trust: TRUST.SIMULATED,
  confidence: null,
  owner: "InQAi POC",
  classification,
  note: note || "Invented for demonstration. Replace with department data.",
});

/* Build a rectangular sector footprint from a centre and a half-extent in
 * kilometres. Generated rather than hand-drawn so every sector is a
 * plausible operational size and none is accidentally a few hundred metres
 * across — the earlier hand-drawn boxes were ~800 m, which is smaller than a
 * single Kumbh sector actually is and far too small to read on the map.
 *
 * 1 deg latitude is ~111 km; longitude shrinks by cos(lat), ~0.94 here. */
function footprint([lng, lat], halfWidthKm, halfHeightKm) {
  const dLat = halfHeightKm / 111;
  const dLng = halfWidthKm / (111 * Math.cos((lat * Math.PI) / 180));
  return [
    [lng - dLng, lat - dLat],
    [lng + dLng, lat - dLat],
    [lng + dLng, lat + dLat],
    [lng - dLng, lat + dLat],
  ];
}

/* ── Sectors ───────────────────────────────────────────────────────────
 * Approximate operational sectors over real neighbourhoods — the footprints
 * are illustrative, the place names and centres are real. capacity/current
 * are simulated figures driving the G/A/R status. */
export const SECTORS = [
  { id: "SEC-01", name: { en: "Ramkund Core", mr: "रामकुंड गाभा" },
    centre: [73.7897, 19.9988], capacity: 100000, current: 92140,
    polygon: footprint([73.7897, 19.9988], 1.5, 1.2) },

  { id: "SEC-02", name: { en: "Panchavati", mr: "पंचवटी" },
    centre: [73.7950, 20.0100], capacity: 120000, current: 81200,
    polygon: footprint([73.7950, 20.0100], 1.8, 1.5) },

  { id: "SEC-03", name: { en: "Trimbakeshwar", mr: "त्र्यंबकेश्वर" },
    centre: [73.5310, 19.9403], capacity: 80000, current: 76400,
    polygon: footprint([73.5310, 19.9403], 1.6, 1.3) },

  { id: "SEC-04", name: { en: "Tapovan", mr: "तपोवन" },
    centre: [73.8110, 20.0205], capacity: 90000, current: 43000,
    polygon: footprint([73.8110, 20.0205], 1.7, 1.4) },

  /* Deliberately sitting in the ELEVATED band (70-88%) and still filling.
     Without this every sector was either comfortable or already critical, so
     the amber state — and the lead-time warning that depends on it — never
     appeared. 54,600 of 70,000 is 78%: elevated, not yet critical, and with
     a decision window still open. */
  { id: "SEC-05", name: { en: "Nashik Road Corridor", mr: "नाशिक रोड मार्गिका" },
    centre: [73.8390, 19.9483], capacity: 70000, current: 54600,
    polygon: footprint([73.8390, 19.9483], 1.5, 1.2) },

  { id: "SEC-06", name: { en: "Gangapur Staging", mr: "गंगापूर स्टेजिंग" },
    centre: [73.6833, 20.0167], capacity: 60000, current: 24800,
    polygon: footprint([73.6833, 20.0167], 1.6, 1.3) },
].map((s) => withProvenance(s, demo(
  "Sector boundary is an approximation drawn for the demo, not the Simhastha " +
  "plan. Occupancy figures are simulated. Replace with NTKMA GeoJSON."
)));

/* ── Ghats ─────────────────────────────────────────────────────────────
 * Real, named bathing ghats on the Godavari. Coordinates public/approximate;
 * the operational attributes hung off them are simulated. */
export const GHATS = [
  { id: "GHAT-01", name: { en: "Ramkund", mr: "रामकुंड" }, coord: [73.7897, 19.9988],
    sector: "SEC-01", principal: true },
  { id: "GHAT-02", name: { en: "Gandhi Talav Ghat", mr: "गांधी तलाव घाट" }, coord: [73.7925, 20.0032],
    sector: "SEC-01", principal: false },
  { id: "GHAT-03", name: { en: "Tapovan Sangam", mr: "तपोवन संगम" }, coord: [73.8125, 20.0212],
    sector: "SEC-04", principal: true },
  { id: "GHAT-04", name: { en: "Kushavarta Kund", mr: "कुशावर्त कुंड" }, coord: [73.5297, 19.9392],
    sector: "SEC-03", principal: true },
  { id: "GHAT-05", name: { en: "Panchavati Ghat", mr: "पंचवटी घाट" }, coord: [73.7962, 20.0078],
    sector: "SEC-02", principal: false },
].map((g) => withProvenance(g, realPlace(
  "Ghat location is a real public coordinate (approximate). Its operational " +
  "attributes — sector assignment, capacity, status — are simulated."
)));

/* ── Gates ─────────────────────────────────────────────────────────────
 * Entirely invented. Real gate positions come from the police deployment plan. */
const GATES_RAW = [
  { id: "GATE-IN-01", kind: "entry", name: { en: "Ramkund Entry 1", mr: "रामकुंड प्रवेश 1" }, coord: [73.7871, 19.9970], sector: "SEC-01" },
  { id: "GATE-IN-02", kind: "entry", name: { en: "Ramkund Entry 2", mr: "रामकुंड प्रवेश 2" }, coord: [73.7930, 19.9968], sector: "SEC-01" },
  { id: "GATE-IN-03", kind: "entry", name: { en: "Panchavati North Entry", mr: "पंचवटी उत्तर प्रवेश" }, coord: [73.7955, 20.0152], sector: "SEC-02" },
  { id: "GATE-IN-04", kind: "entry", name: { en: "Trimbak West Entry", mr: "त्र्यंबक पश्चिम प्रवेश" }, coord: [73.5262, 19.9410], sector: "SEC-03" },
  { id: "GATE-IN-05", kind: "entry", name: { en: "Tapovan Entry", mr: "तपोवन प्रवेश" }, coord: [73.8060, 20.0180], sector: "SEC-04" },
  { id: "GATE-OUT-01", kind: "exit", name: { en: "Ramkund Exit A", mr: "रामकुंड निर्गमन अ" }, coord: [73.7912, 20.0012], sector: "SEC-01" },
  { id: "GATE-OUT-02", kind: "exit", name: { en: "Panchavati Exit", mr: "पंचवटी निर्गमन" }, coord: [73.8012, 20.0060], sector: "SEC-02" },
  { id: "GATE-OUT-03", kind: "exit", name: { en: "Trimbak East Exit", mr: "त्र्यंबक पूर्व निर्गमन" }, coord: [73.5362, 19.9425], sector: "SEC-03" },
  { id: "GATE-OUT-04", kind: "exit", name: { en: "Tapovan Exit", mr: "तपोवन निर्गमन" }, coord: [73.8172, 20.0242], sector: "SEC-04" },
];
export const ENTRY_GATES = GATES_RAW.filter((g) => g.kind === "entry")
  .map((g) => withProvenance(g, demo("Gate position invented for demo.")));
export const EXIT_GATES = GATES_RAW.filter((g) => g.kind === "exit")
  .map((g) => withProvenance(g, demo("Gate position invented for demo.")));

/* ── Facilities ────────────────────────────────────────────────────────
 * Hospitals and the railway station are real places; camps, toilets, water
 * points, posts are invented placements near real areas. */
const FAC = (id, layer, name, coord, sector, prov) =>
  withProvenance({ id, layer, name, coord, sector }, prov);

export const FACILITIES = [
  // Real institutions
  FAC("HOSP-01", "hospitals", { en: "Nashik Civil Hospital", mr: "नाशिक सिव्हिल रुग्णालय" },
      [73.7880, 19.9930], "SEC-01", realPlace("Real hospital. Capacity figures simulated.")),
  FAC("HOSP-02", "hospitals", { en: "Trimbakeshwar Rural Hospital", mr: "त्र्यंबकेश्वर ग्रामीण रुग्णालय" },
      [73.5330, 19.9430], "SEC-03", realPlace("Real hospital. Capacity figures simulated.")),
  FAC("TRN-01", "transportPoints", { en: "Nashik Road Railway Station", mr: "नाशिक रोड रेल्वे स्थानक" },
      [73.8390, 19.9483], "SEC-05", realPlace("Real station.")),

  // Invented operational assets
  FAC("CMD-01", "commandCentres", { en: "Integrated Command Centre — Nashik", mr: "एकात्मिक कमांड सेंटर — नाशिक" },
      [73.7905, 19.9945], "SEC-01", demo("Invented location.", CLASSIFICATION.RESTRICTED)),
  FAC("CMD-02", "commandCentres", { en: "Sector Command — Trimbakeshwar", mr: "सेक्टर कमांड — त्र्यंबकेश्वर" },
      [73.5318, 19.9418], "SEC-03", demo("Invented location.", CLASSIFICATION.RESTRICTED)),
  FAC("POL-01", "policePosts", { en: "Police Post — Ramkund", mr: "पोलीस चौकी — रामकुंड" },
      [73.7889, 19.9998], "SEC-01", demo("Invented deployment.", CLASSIFICATION.RESTRICTED)),
  FAC("POL-02", "policePosts", { en: "Police Post — Panchavati", mr: "पोलीस चौकी — पंचवटी" },
      [73.7975, 20.0105], "SEC-02", demo("Invented deployment.", CLASSIFICATION.RESTRICTED)),
  FAC("POL-03", "policePosts", { en: "Police Post — Kushavarta", mr: "पोलीस चौकी — कुशावर्त" },
      [73.5305, 19.9400], "SEC-03", demo("Invented deployment.", CLASSIFICATION.RESTRICTED)),
  FAC("FIRE-01", "fireStations", { en: "Fire Station — Panchavati", mr: "अग्निशमन केंद्र — पंचवटी" },
      [73.7998, 20.0088], "SEC-02", demo("Invented location.")),
  FAC("MED-01", "medicalCamps", { en: "Medical Camp — Ramkund", mr: "वैद्यकीय शिबिर — रामकुंड" },
      [73.7908, 19.9978], "SEC-01", demo("Invented camp.")),
  FAC("MED-02", "medicalCamps", { en: "Medical Camp — Tapovan", mr: "वैद्यकीय शिबिर — तपोवन" },
      [73.8118, 20.0198], "SEC-04", demo("Invented camp.")),
  FAC("AMB-01", "ambulancePoints", { en: "Ambulance Point — Ramkund", mr: "रुग्णवाहिका केंद्र — रामकुंड" },
      [73.7880, 19.9992], "SEC-01", demo("Invented placement.")),
  FAC("AMB-02", "ambulancePoints", { en: "Ambulance Point — Trimbak", mr: "रुग्णवाहिका केंद्र — त्र्यंबक" },
      [73.5288, 19.9398], "SEC-03", demo("Invented placement.")),
  FAC("PARK-01", "parking", { en: "Parking P1 — Gangapur", mr: "वाहनतळ P1 — गंगापूर" },
      [73.6840, 20.0170], "SEC-06", demo("Invented placement.")),
  FAC("PARK-02", "parking", { en: "Parking P2 — Nashik Road", mr: "वाहनतळ P2 — नाशिक रोड" },
      [73.8360, 19.9460], "SEC-05", demo("Invented placement.")),
  FAC("BUS-01", "transportPoints", { en: "Shuttle Hub — Tapovan", mr: "शटल हब — तपोवन" },
      [73.8085, 20.0225], "SEC-04", demo("Invented placement.")),
  FAC("WC-01", "toilets", { en: "Toilet Block A", mr: "स्वच्छतागृह अ" },
      [73.7885, 19.9975], "SEC-01", demo("Invented placement.")),
  FAC("WC-02", "toilets", { en: "Toilet Block B", mr: "स्वच्छतागृह ब" },
      [73.7968, 20.0090], "SEC-02", demo("Invented placement.")),
  FAC("WTR-01", "waterPoints", { en: "Water Point 1", mr: "जल केंद्र 1" },
      [73.7902, 19.9995], "SEC-01", demo("Invented placement.")),
  FAC("WTR-08", "waterPoints", { en: "Water Point 8 — Panchavati", mr: "जल केंद्र 8 — पंचवटी" },
      [73.7988, 20.0112], "SEC-02", demo("Invented placement.")),
  FAC("HELP-01", "helpCentres", { en: "Lost & Found — Ramkund", mr: "हरवले-सापडले — रामकुंड" },
      [73.7918, 19.9985], "SEC-01", demo("Invented placement.")),
  FAC("HELP-02", "helpCentres", { en: "Help Centre — Trimbak", mr: "मदत केंद्र — त्र्यंबक" },
      [73.5325, 19.9408], "SEC-03", demo("Invented placement.")),
];

/* ── Routes ────────────────────────────────────────────────────────────
 * Schematic corridors between real places. NOT routed on the road network —
 * a real deployment should snap these to OSM ways or department route plans. */
const ROUTE = (id, layer, name, coords, prov) =>
  withProvenance({ id, layer, name, coords }, prov);

export const ROUTES = [
  ROUTE("PED-01", "pedestrianRoutes", { en: "Ramkund → Panchavati pilgrim corridor", mr: "रामकुंड → पंचवटी मार्गिका" },
    [[73.7897,19.9988],[73.7920,20.0025],[73.7950,20.0070],[73.7962,20.0100]],
    demo("Schematic corridor, not road-network routed.")),
  ROUTE("PED-02", "pedestrianRoutes", { en: "Tapovan approach walk", mr: "तपोवन पदपथ" },
    [[73.8060,20.0180],[73.8095,20.0198],[73.8125,20.0212]],
    demo("Schematic corridor, not road-network routed.")),
  ROUTE("VEH-01", "vehicleRoutes", { en: "Nashik Road → Ramkund vehicle route", mr: "नाशिक रोड → रामकुंड वाहन मार्ग" },
    [[73.8390,19.9483],[73.8200,19.9600],[73.8000,19.9800],[73.7900,19.9950]],
    demo("Schematic corridor, not road-network routed.")),
  ROUTE("VEH-02", "vehicleRoutes", { en: "Nashik → Trimbakeshwar highway", mr: "नाशिक → त्र्यंबकेश्वर महामार्ग" },
    [[73.7700,19.9900],[73.7100,19.9700],[73.6400,19.9550],[73.5600,19.9420],[73.5330,19.9405]],
    demo("Schematic corridor, not road-network routed.")),
  ROUTE("DIV-01", "diversionRoutes", { en: "Diversion — Gangapur bypass", mr: "वळण — गंगापूर बायपास" },
    [[73.7900,19.9950],[73.7400,20.0050],[73.6900,20.0160]],
    demo("Illustrative diversion. Not an approved traffic plan.")),
  ROUTE("EVAC-01", "evacuationRoutes", { en: "Evacuation — Ramkund to Gangapur staging", mr: "निर्वासन — रामकुंड ते गंगापूर" },
    [[73.7897,19.9988],[73.7600,20.0060],[73.7200,20.0130],[73.6850,20.0168]],
    demo("Illustrative evacuation line. NOT an approved evacuation plan — " +
         "approved routes must come from the district disaster management authority.")),
  ROUTE("EVAC-02", "evacuationRoutes", { en: "Evacuation — Trimbak to Nashik", mr: "निर्वासन — त्र्यंबक ते नाशिक" },
    [[73.5310,19.9403],[73.6000,19.9480],[73.6800,19.9620],[73.7500,19.9800]],
    demo("Illustrative evacuation line. NOT an approved evacuation plan.")),
];

/* ── Congregation areas ────────────────────────────────────────────────── */
export const CONGREGATION = [
  { id: "CONG-01", name: { en: "Shahi Snan assembly — Ramkund", mr: "शाही स्नान मेळावा — रामकुंड" },
    polygon: [[73.7875,19.9975],[73.7925,19.9975],[73.7928,20.0008],[73.7878,20.0008]] },
  { id: "CONG-02", name: { en: "Akhara camp ground — Tapovan", mr: "आखाडा तळ — तपोवन" },
    polygon: [[73.8075,20.0185],[73.8155,20.0185],[73.8158,20.0240],[73.8078,20.0238]] },
].map((c) => withProvenance(c, demo("Assembly footprint invented for demo.")));

/* Every geo record, flattened — the search index (§8) builds from this so a
 * new layer becomes searchable automatically. */
export function allGeoRecords() {
  return [
    ...SECTORS.map((s) => ({ ...s, layer: "sectors", coord: s.centre, kind: "Sector" })),
    ...GHATS.map((g) => ({ ...g, layer: "ghats", kind: "Ghat" })),
    ...ENTRY_GATES.map((g) => ({ ...g, layer: "entryGates", kind: "Entry gate" })),
    ...EXIT_GATES.map((g) => ({ ...g, layer: "exitGates", kind: "Exit gate" })),
    ...FACILITIES.map((f) => ({ ...f, kind: "Facility" })),
    ...ROUTES.map((r) => ({ ...r, coord: r.coords[0], kind: "Route" })),
    ...CONGREGATION.map((c) => ({ ...c, layer: "congregation", coord: c.polygon[0], kind: "Congregation area" })),
  ];
}

/* Zone status from occupancy — the G/A/R rule in §2. Thresholds are the
 * single place the bands are defined. */
export const STATUS_THRESHOLDS = { amber: 0.7, red: 0.88 };
export function sectorStatus(sector) {
  const ratio = sector.current / sector.capacity;
  if (ratio >= STATUS_THRESHOLDS.red) return "red";
  if (ratio >= STATUS_THRESHOLDS.amber) return "amber";
  return "green";
}
export const STATUS_COLOR = { green: "#10B981", amber: "#F59E0B", red: "#EF4444" };
